import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useStepWorkspaceRevision } from '../context/StepWorkspaceContext';
import { Link } from 'react-router-dom';
import Header, { BreadcrumbItem } from '../components/Header';
import ContentReader from '../components/ContentReader';
import HistoryButton from '../components/HistoryButton';
import StepHelpButton from '../components/StepHelpButton';
import HistoryModal from '../components/HistoryModal';
import VideoModal from '../components/VideoModal';
import { progressService } from '../services/progressService';
import { useAuthErrorHandler } from '../hooks/useAuthErrorHandler';
import { useRelocalizeStepActions } from '../hooks/useRelocalizeStepActions';
import { AuthErrorDisplay } from '../components/AuthErrorDisplay';
import { mergeStepActions, stepDataWithActions, buildStepDataWithActions } from '../utils/mergeStepActions';
import { getStepActionSchema } from '../config/stepActionSchemas';
import styles from '../styles/stepCommon.module.css';
import StepActions, { ActionItem } from '../components/StepActions';
import StepLoginPrompt from '../components/StepLoginPrompt';
import Step1WritingModal from '../components/Step1WritingModal';
import Step1PresentationFields from '../components/Step1PresentationFields';
import {
  normalizeStep1Presentation,
  Step1PresentationRecord,
} from '../types/step1Presentation';
import { Step1WritingMetadata, isStep1PathSelected } from '../types/step1Writing';
import { migrateStep1WritingIfNeeded } from '../utils/step1WritingMigration';
import { formatStep1ProgressLine } from '../utils/step1WritingProgressSummary';
import { perfMark, perfSummary, perfTimeAsync } from '../utils/perfTimer';
import nudgeStyles from '../styles/threeCirclesModal.module.css';
import { STEP_UI } from '../config/uiStrings';
import { useLocale } from '../context/LocaleContext';
import { getStepActionLabel } from '../config/stepActionLabels';
import { getStepActionDescription } from '../config/stepActionDescriptions';
import {
  getActionsIntro,
  getActionsToCompleteTitle,
  getCongratsBody,
  getCongratsHeading,
  getNextStepNavLabel,
  getPrevStepNavLabel,
  getRefreshPageLabel,
  getStepBreadcrumbs,
  getStepLoadingMessage,
  getStepPageQuote,
  getStepPageTitle,
  getVideoGuideTitle,
} from '../config/stepPageCopy';

const GETTING_STARTED_NUDGE_KEY = 'dismissed-getting-started-nudge';

interface Step1Props {
  user?: any;
  onLogin: () => void;
  onLogout: () => void;
}

const Step1: React.FC<Step1Props> = ({ user, onLogin, onLogout }) => {
  const stepWorkspaceRevision = useStepWorkspaceRevision();
  const { t } = useLocale();
  const [isContentReaderOpen, setIsContentReaderOpen] = useState(false);
  const [isGuidanceOpen, setIsGuidanceOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isWritingModalOpen, setIsWritingModalOpen] = useState(false);
  const [collapsedActions, setCollapsedActions] = useState<Set<number>>(new Set());
  // Define the original action structure
  const originalActions: ActionItem[] = [
    {
      id: 1,
      title: getStepActionLabel(1, 1),
      description: getStepActionDescription(1, 1),
      completed: false,
      hasButton: true,
      buttonText: STEP_UI.READ_GREEN_BOOK,
      buttonAction: () => setIsContentReaderOpen(true)
    },
    {
      id: 2,
      title: getStepActionLabel(1, 2),
      description: getStepActionDescription(1, 2),
      completed: false,
      buttons: [
        {
          text: STEP_UI.READ_GUIDANCE,
          action: () => setIsGuidanceOpen(true),
          variant: 'primary'
        },
        {
          text: STEP_UI.WATCH_VIDEO,
          action: () => setIsVideoModalOpen(true),
          variant: 'secondary'
        }
      ]
    },
    {
      id: 3,
      title: getStepActionLabel(1, 3),
      description: getStepActionDescription(1, 3),
      completed: false,
      hasButton: true,
      buttonText: STEP_UI.FIRST_STEP_WRITING_WORKSPACE,
      buttonAction: () => setIsWritingModalOpen(true)
    },
    {
      id: 4,
      title: getStepActionLabel(1, 4),
      description: getStepActionDescription(1, 4),
      completed: false
    },
    {
      id: 5,
      title: getStepActionLabel(1, 5),
      description: getStepActionDescription(1, 5),
      completed: false
    },
    {
      id: 6,
      title: getStepActionLabel(1, 6),
      description: getStepActionDescription(1, 6),
      completed: false
    }
  ];

  const [actions, setActions] = useState<ActionItem[]>(originalActions);
  useRelocalizeStepActions(originalActions, setActions);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [stepData, setStepData] = useState<any>(null);
  const [isOptedOut, setIsOptedOut] = useState(false);
  const [gettingStartedComplete, setGettingStartedComplete] = useState(true);
  const [nudgeDismissed, setNudgeDismissed] = useState(
    () => localStorage.getItem(GETTING_STARTED_NUDGE_KEY) === '1'
  );
  const [step1Writing, setStep1Writing] = useState<Step1WritingMetadata | null>(null);
  const [step1Presentation, setStep1Presentation] = useState<Step1PresentationRecord>({});
  const presentationSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { authError, errorMessage, handleError, clearError } = useAuthErrorHandler();

  const breadcrumbs: BreadcrumbItem[] = getStepBreadcrumbs(1);

  // Load progress for steps 0 + 1 and journey preference in one pass
  const loadPageData = async () => {
    if (!user) {
      setIsInitializing(false);
      return;
    }

    setIsInitializing(true);

    try {
      perfMark('Step1', 'loadPageData start');
      const { steps, journeyProgress } = await perfTimeAsync('Step1 loadPageData (total)', () =>
        progressService.loadStepPagesProgress('SAA', '12-steps', [0, 1], {
          migrateGettingStarted: true,
          includeJourney: true,
        })
      );

      const gettingStarted = steps[0] ?? null;
      const existingProgress = steps[1] ?? null;

      setGettingStartedComplete(!!gettingStarted?.completed);

      if (journeyProgress) {
        setIsOptedOut(journeyProgress.opted_out);
      }

      if (existingProgress) {
        const savedData = existingProgress.data || {};
        setStepData(existingProgress);
        setStep1Presentation(normalizeStep1Presentation(savedData.step1_presentation));

        const { metadata: writingMeta, applied: writingMigration } = migrateStep1WritingIfNeeded(
          savedData,
          true
        );
        setStep1Writing(writingMeta);

        if (savedData.actions) {
          const merged = mergeStepActions(1, originalActions, savedData, !!existingProgress.completed);
          setActions(merged.actions);
          if (merged.migrationApplied || writingMigration) {
            await progressService.updateStepProgress({
              program: 'SAA',
              track: '12-steps',
              step_number: 1,
              step_title: 'Step One',
              completed: !!existingProgress.completed,
              data: buildStepDataWithActions(merged.actions, merged.actionSchemaVersion, {
                ...savedData,
                step1_writing: writingMeta,
              }),
            });
          }
        } else if (writingMigration) {
          await progressService.updateStepProgress({
            program: 'SAA',
            track: '12-steps',
            step_number: 1,
            step_title: 'Step One',
            completed: !!existingProgress.completed,
            data: { ...savedData, step1_writing: writingMeta },
          });
        }
      } else {
        const { metadata: writingMeta } = migrateStep1WritingIfNeeded(null, false);
        setStep1Writing(writingMeta);
      }

      clearError();
      perfSummary('Step One page');
    } catch (err: any) {
      console.error('Error loading step progress:', err);
      handleError(err, t('step.page.error.load'));
    } finally {
      setIsInitializing(false);
    }
  };

  useEffect(() => {
    loadPageData();
  }, [user, stepWorkspaceRevision]);

  const getCurrentActionIndex = () => {
    return actions.findIndex(action => !action.completed);
  };

  const highestCompletedIndex = (() => {
    let idx = -1;
    actions.forEach((action, i) => {
      if (action.completed) idx = i;
    });
    return idx;
  })();

  const isActionAvailable = (actionIndex: number) => {
    if (actionIndex === 0) return true;
    return actions.slice(0, actionIndex).every(a => a.completed);
  };

  const buildStepData = (actionsList: ActionItem[], presentation?: Step1PresentationRecord) => {
    const schemaVersion = getStepActionSchema(1).actionSchemaVersion;
    const extra: Record<string, unknown> = {};
    if (step1Writing) extra.step1_writing = step1Writing;
    const record = presentation ?? step1Presentation;
    const normalized = normalizeStep1Presentation(record);
    if (normalized.presentedDate || normalized.place || normalized.reflection) {
      extra.step1_presentation = normalized;
    }
    return stepDataWithActions(actionsList, schemaVersion, extra);
  };

  const persistPresentation = useCallback(
    async (next: Step1PresentationRecord) => {
      if (!user) return;
      const normalized = normalizeStep1Presentation({
        ...next,
        updatedAt: new Date().toISOString(),
      });
      setStep1Presentation(normalized);
      try {
        const baseData = { ...(stepData?.data || {}), step1_writing: step1Writing };
        if (normalized.presentedDate || normalized.place || normalized.reflection) {
          baseData.step1_presentation = normalized;
        } else {
          delete baseData.step1_presentation;
        }
        const updatedStepData = await progressService.updateStepProgress({
          program: 'SAA',
          track: '12-steps',
          step_number: 1,
          step_title: 'Step One',
          completed: actions.every((a) => a.completed),
          data: buildStepDataWithActions(actions, getStepActionSchema(1).actionSchemaVersion, baseData),
        });
        if (updatedStepData?.step) setStepData(updatedStepData.step);
      } catch (err: unknown) {
        handleError(err, t('step.1.page.error.save_presentation'));
      }
    },
    [user, actions, stepData, step1Writing, handleError]
  );

  const handlePresentationChange = useCallback(
    (next: Step1PresentationRecord) => {
      setStep1Presentation(next);
      if (presentationSaveTimer.current) clearTimeout(presentationSaveTimer.current);
      presentationSaveTimer.current = setTimeout(() => {
        void persistPresentation(next);
      }, 500);
    },
    [persistPresentation]
  );

  useEffect(() => {
    return () => {
      if (presentationSaveTimer.current) clearTimeout(presentationSaveTimer.current);
    };
  }, []);

  const handleSaveStep1Writing = async (metadata: Step1WritingMetadata) => {
    setStep1Writing(metadata);
    try {
      const updatedStepData = await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 1,
        step_title: 'Step One',
        completed: actions.every((a) => a.completed),
        data: buildStepDataWithActions(actions, getStepActionSchema(1).actionSchemaVersion, {
          ...(stepData?.data || {}),
          step1_writing: metadata,
        }),
      });
      if (updatedStepData?.step) setStepData(updatedStepData.step);
    } catch (err: unknown) {
      handleError(err, t('step.1.page.error.save_writing'));
    }
  };

  const handleCheckboxChange = async (actionId: number, checked: boolean) => {
    const updatedActions = actions.map(action =>
      action.id === actionId ? { ...action, completed: checked } : action
    );
    setActions(updatedActions);

    try {
      await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 1,
        step_title: 'Step One',
        completed: updatedActions.every(action => action.completed),
        data: buildStepData(updatedActions),
      });
    } catch (err: any) {
      console.error('Error updating action progress:', err);
      handleError(err, t('step.page.error.save_progress'));
    }
  };

  const handleButtonClick = (action: ActionItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (action.buttonAction) {
      action.buttonAction();
    }
  };

  const toggleActionCollapse = (actionId: number) => {
    const newCollapsed = new Set(collapsedActions);
    if (newCollapsed.has(actionId)) {
      newCollapsed.delete(actionId);
    } else {
      newCollapsed.add(actionId);
    }
    setCollapsedActions(newCollapsed);
  };

  // Handle action changes and save to backend
  const handleActionChange = async (updatedActions: ActionItem[]) => {
    if (!user) {
      handleError(new Error('User not logged in'), t('step.page.error.sign_in'));
      return;
    }

    try {
      // Update local state
      setActions(updatedActions);

      // Save to backend
      const allCompleted = updatedActions.every(action => action.completed);
      const updatedStepData = await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 1,
        step_title: 'Step One',
        completed: allCompleted,
        data: buildStepData(updatedActions),
      });
      
      // Update step data for history modal
      if (updatedStepData && updatedStepData.step) {
        setStepData(updatedStepData.step);
      }



      // Update status note
      const completedCount = updatedActions.filter(a => a.completed).length;
      await progressService.updateStatus({
        program: 'SAA',
        track: '12-steps',
        status_note: `Working Step One - ${completedCount} of 6 actions completed`,
        progress_milestone: allCompleted ? 'step1_completed' : 'step1_in_progress'
      });

    } catch (err: any) {
      console.error('Error updating action progress:', err);
      handleError(err, t('step.page.error.save_progress'));
    }
  };

  const allCompleted = actions.every(action => action.completed);

  return (
    <div className={styles.stepPage}>
      <Header 
        breadcrumbs={breadcrumbs}
        user={user}
        onLogin={onLogin}
        onLogout={onLogout}
      />
      
      <main className={styles.mainContent}>
        <div className={styles.pageHeader}>
          <div className={styles.stepTitleContainer}>
            <h1 className={styles.stepTitle}>{getStepPageTitle(1)}</h1>
            <StepHelpButton stepKey="app" />
            {stepData && (
              <HistoryButton
                stepNumber={1}
                stepTitle={getStepPageTitle(1)}
                stepData={stepData}
                isOptedOut={isOptedOut}
                onStepUpdated={() => setIsHistoryModalOpen(true)}
              />
            )}
          </div>
          <p className={styles.stepSubtitle}>{getStepPageQuote(1)}
          </p>
        </div>

        {!isInitializing && user && !gettingStartedComplete && !nudgeDismissed && (
          <div className={nudgeStyles.gettingStartedNudge}>
            <p>
              {t('step.1.page.nudge_lead')}{' '}
              <Link to="/saa/12-steps/getting-started">{t('getting_started.page.title')}</Link>{' '}
              {t('step.1.page.nudge_tail')}
            </p>
            <button
              type="button"
              className={nudgeStyles.gettingStartedNudgeDismiss}
              onClick={() => {
                localStorage.setItem(GETTING_STARTED_NUDGE_KEY, '1');
                setNudgeDismissed(true);
              }}
              aria-label={t('step.1.page.nudge_dismiss_aria')}
            >
              ×
            </button>
          </div>
        )}

        {errorMessage && (
          <div className={styles.errorMessage}>
            {authError ? (
              <AuthErrorDisplay
                error={errorMessage}
                onLogin={onLogin}
                onRefresh={() => window.location.reload()}
              />
            ) : (
              <>
                <p>{errorMessage}</p>
                <button onClick={() => window.location.reload()}>{getRefreshPageLabel()}</button>
              </>
            )}
          </div>
        )}

        {!user && !isInitializing && !errorMessage && <StepLoginPrompt onLogin={onLogin} />}

        {user && isInitializing && !errorMessage && (
          <div className={styles.loadingMessage}>
            <p>{getStepLoadingMessage()}</p>
          </div>
        )}

        {user && !isInitializing && !errorMessage && (
          <div className={styles.actionsContainer}>
            <h2 className={styles.actionsTitle}>{getActionsToCompleteTitle(1)}</h2>
            <p className={styles.actionsDescription}>{getActionsIntro()}</p>

            <StepActions
              actions={actions.map((a, index) => {
                if (a.id === 3) {
                  return {
                    ...a,
                    renderBelowButton: () => {
                      if (!step1Writing || !isStep1PathSelected(step1Writing)) return null;
                      const line = formatStep1ProgressLine(step1Writing);
                      return line ? <span>{line}</span> : null;
                    },
                  };
                }
                if (a.id === 5 && (isActionAvailable(index) || a.completed)) {
                  return {
                    ...a,
                    renderBelowButton: () => (
                      <Step1PresentationFields
                        value={step1Presentation}
                        onChange={handlePresentationChange}
                      />
                    ),
                  };
                }
                return a;
              })}
              onActionChange={handleActionChange}
            />

            {allCompleted && (
              <>
                <div className={styles.completionMessage}>
                  <h3>{getCongratsHeading()}</h3>
                  <p>{getCongratsBody(1)}</p>
                </div>
                <div className={styles.stepNavigation}>
                  <div className={styles.navigationLinks}>
                    <a href="/saa/12-steps/getting-started" className={styles.prevStepLink}>
                      {getPrevStepNavLabel(1)}
                    </a>
                    <a href="/saa/12-steps/step-2" className={styles.nextStepLink}>
                      {getNextStepNavLabel(1)}
                    </a>
                  </div>
                </div>
              </>
            )}

            {!allCompleted && (
              <div className={styles.stepNavigation}>
                <div className={styles.navigationLinks}>
                  <a href="/saa/12-steps/getting-started" className={styles.prevStepLink}>
                    {getPrevStepNavLabel(1)}
                  </a>
                  <a href="/saa/12-steps/step-2" className={styles.nextStepLink}>
                    {getNextStepNavLabel(1)}
                  </a>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* ContentReader for Understanding Step One */}
      <ContentReader
        isOpen={isContentReaderOpen}
        onClose={() => setIsContentReaderOpen(false)}
        filePath="/content/010_CHAPTER_THREE.md"
        startTag="<<step-one start>>"
        endTag="<<step-one end>>"
      />

      {/* ContentReader for Working Step One */}
      <ContentReader
        isOpen={isGuidanceOpen}
        onClose={() => setIsGuidanceOpen(false)}
        filePath="/content/Step_1_Guidance.md"
        startTag="<<working-step-one start>>"
        endTag="<<working-step-one end>>"
      />

      {/* History Modal */}
      <HistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        stepNumber={1}
        stepTitle={getStepPageTitle(1)}
        stepData={stepData}
        onStepUpdated={() => {
          setIsHistoryModalOpen(false);
          // Refresh step data
          if (user) {
            loadPageData();
          }
        }}
      />
      
      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        videoId="J6-8SICtjww"
        title={getVideoGuideTitle(1)}
      />
      {step1Writing && (
        <Step1WritingModal
          isOpen={isWritingModalOpen}
          onClose={() => setIsWritingModalOpen(false)}
          user={user}
          metadata={step1Writing}
          onSaveMetadata={handleSaveStep1Writing}
        />
      )}
    </div>
  );
};

export default Step1; 
